import { useMemo } from 'react';
import type { PoolCalculateResponse, PoolConfiguration } from '@/api/calculators/types';
import { rs } from './constants';

export const SELECT_CLASS =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200';
export const INPUT_CLASS =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200';

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-slate-600">{label}</label>
      {children}
    </div>
  );
}

export function LeadField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  testId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  testId?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
        data-testid={testId}
      />
    </div>
  );
}

export function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-stone-100 px-2.5 py-2">
      <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-base font-bold text-slate-900">{value}</div>
    </div>
  );
}

export function StockPill({ inStock }: { inStock: boolean }) {
  return (
    <span
      data-testid={inStock ? 'pool-stock-in' : 'pool-stock-non'}
      className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${
        inStock
          ? 'bg-emerald-100 text-emerald-800'
          : 'bg-amber-100 text-amber-800'
      }`}
    >
      {inStock ? '● In stock' : '⏱ Non-stock'}
    </span>
  );
}

export function KvRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between rounded bg-black/5 px-2 py-1">
      <span className="text-slate-500">{label}</span>
      <span className="font-mono font-semibold">{value}</span>
    </div>
  );
}

export function HeatUpRow({
  heatUp,
}: {
  heatUp: PoolConfiguration['estimated_heat_up'];
}) {
  return (
    <div className="mt-2 flex justify-between rounded-md bg-blue-50 px-2.5 py-1.5">
      <span className="text-[10px] font-bold uppercase text-blue-700">
        Heat-up
      </span>
      <span className="font-mono text-xs font-bold">
        {heatUp.hours_low}–{heatUp.hours_high} hr
        {heatUp.within_target ? ' ✓' : ''}
      </span>
    </div>
  );
}

export function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-white p-4 shadow-sm">
      <h3 className="mb-2 text-sm font-bold text-slate-900">{title}</h3>
      {children}
    </div>
  );
}

export function ProfileRow({
  label,
  value,
  sub,
  warn,
}: {
  label: string;
  value: string;
  sub?: string;
  warn?: boolean;
}) {
  return (
    <>
      <div className="text-slate-600">{label}</div>
      <div className="text-right font-mono font-semibold">
        {value}
        {sub && (
          <span
            className={`ml-1 text-[10px] ${warn ? 'text-amber-700' : 'text-slate-400'}`}
          >
            {sub}
          </span>
        )}
      </div>
    </>
  );
}

export function CmpRow({
  label,
  hp,
  lpg,
  diesel,
  electric,
}: {
  label: string;
  hp: string;
  lpg: string;
  diesel: string;
  electric: string;
}) {
  return (
    <tr className="border-b border-stone-100 last:border-none">
      <td className="py-1.5 text-slate-600">{label}</td>
      <td className="bg-emerald-50 py-1.5 text-center font-mono font-semibold text-emerald-900">
        {hp}
      </td>
      <td className="py-1.5 text-center font-mono">{lpg}</td>
      <td className="py-1.5 text-center font-mono">{diesel}</td>
      <td className="py-1.5 text-center font-mono">{electric}</td>
    </tr>
  );
}

export function SavingsRow({
  label,
  value,
  pct,
  color,
  payback,
}: {
  label: string;
  value: number;
  pct: number;
  color: string;
  payback: number | null;
}) {
  return (
    <div className="mb-2 last:mb-0">
      <div className="flex justify-between">
        <span className="text-xs font-medium">{label}</span>
        <span className="font-mono text-sm font-bold">
          {rs(value)}
          <span className="ml-1 text-[10px] text-slate-500">/yr</span>
        </span>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded-full bg-stone-200">
        <div
          className="h-full"
          style={{
            width: `${Math.max(0, pct).toFixed(0)}%`,
            background: color,
          }}
        />
      </div>
      {payback !== null && payback >= 0 && (
        <div className="mt-1 text-[11px] text-slate-500">
          Capex payback:{' '}
          <strong>
            {payback <= 0 ? 'immediate' : `${payback.toFixed(1)} yrs`}
          </strong>
        </div>
      )}
    </div>
  );
}

export function TcoSection({
  title,
  years,
  tco,
}: {
  title: string;
  years: number;
  tco: {
    heat_pump: number;
    lpg_boiler: number;
    diesel_boiler: number;
    electric_immersion: number;
    net_savings_vs_lpg: number;
  };
}) {
  const max = Math.max(
    tco.heat_pump,
    tco.lpg_boiler,
    tco.diesel_boiler,
    tco.electric_immersion,
  );
  const rows: Array<[string, number, string, boolean]> = [
    ['Sun Stellar Heat Pump', tco.heat_pump, '#16a34a', true],
    ['LPG Boiler', tco.lpg_boiler, '#D97706', false],
    ['Diesel Boiler', tco.diesel_boiler, '#7C2D12', false],
    ['Electric Immersion', tco.electric_immersion, '#1E40AF', false],
  ];
  return (
    <Section title={title}>
      <table className="mb-3 w-full text-[11.5px]">
        <tbody>
          <tr className="bg-slate-900 font-bold text-white">
            <td className="py-2 pl-3">Total {years}-Yr TCO</td>
            <td className="bg-emerald-700 py-2 text-center font-mono">
              {rs(tco.heat_pump)} ✓
            </td>
            <td className="py-2 text-center font-mono">{rs(tco.lpg_boiler)}</td>
            <td className="py-2 text-center font-mono">
              {rs(tco.diesel_boiler)}
            </td>
            <td className="py-2 text-center font-mono">
              {rs(tco.electric_immersion)}
            </td>
          </tr>
        </tbody>
      </table>
      {rows.map(([lbl, val, col, win]) => {
        const pct = (val / max) * 100;
        return (
          <div key={lbl} className="mb-2 last:mb-0">
            <div className="flex justify-between">
              <span className={`text-xs ${win ? 'font-bold' : ''}`}>
                {lbl}
                {win && ' ✓ best'}
              </span>
              <span className="font-mono text-sm font-bold">{rs(val)}</span>
            </div>
            <div className="mt-1 h-2 overflow-hidden rounded-full bg-stone-200">
              <div
                className="h-full"
                style={{ width: `${pct.toFixed(0)}%`, background: col }}
              />
            </div>
          </div>
        );
      })}
      {tco.net_savings_vs_lpg > 0 && (
        <div className="mt-2 border-t border-stone-200 pt-2 text-xs">
          {years}-yr net savings vs LPG:{' '}
          <strong className="font-mono text-emerald-700">
            {rs(tco.net_savings_vs_lpg)}
          </strong>
        </div>
      )}
    </Section>
  );
}

export function BuiltInFeaturesPanel({
  features,
}: {
  features: PoolCalculateResponse['sizing']['builtin_features'];
}) {
  const items = useMemo(
    () =>
      (features ?? []).map((f) =>
        typeof f === 'string' ? { title: f, description: '' } : f,
      ),
    [features],
  );
  if (!items.length) return null;
  return (
    <details className="rounded-lg border border-stone-200">
      <summary className="cursor-pointer rounded-lg bg-stone-50 px-3 py-2 text-xs font-semibold text-slate-700">
        Built-in features ({items.length})
      </summary>
      <ul className="space-y-2 px-4 py-3 text-xs leading-relaxed text-slate-700">
        {items.map((f, i) => (
          <li key={i}>
            <strong>{f.title}</strong>
            {f.description ? `: ${f.description}` : ''}
          </li>
        ))}
      </ul>
    </details>
  );
}

export function EngineeringNotesPanel({
  notes,
}: {
  notes: PoolCalculateResponse['sizing']['engineering_notes'];
}) {
  const items = useMemo(
    () =>
      (notes ?? []).map((n) =>
        typeof n === 'string' ? { title: n, description: '' } : n,
      ),
    [notes],
  );
  if (!items.length) return null;
  return (
    <details className="rounded-lg border border-stone-200">
      <summary className="cursor-pointer rounded-lg bg-stone-50 px-3 py-2 text-xs font-semibold text-slate-700">
        Engineering notes ({items.length})
      </summary>
      <ol className="space-y-2 px-4 py-3 text-xs leading-relaxed text-slate-700">
        {items.map((n, i) => (
          <li key={i}>
            <strong>{n.title}</strong>
            {n.description ? `: ${n.description}` : ''}
          </li>
        ))}
      </ol>
    </details>
  );
}
